import HighlightedTextOutput from 'Components/Core/HighlightedTextOutput';
import { WorksheetCollatedMessagesButton } from 'Components/Worksheets/Core';
import { getMessagesForMultiplePaths } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import { splitIntoSearchTokens } from 'Lib/String/splitIntoSearchTokens';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { MoistureContentWetMassGridBalanceKey } from '.';
import styles from '../../../Styles/Util/_variables.module.scss';
import { Dropdown, Field, NumericInput, TextInput, TextOutput } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import { WorksheetTopLevelLink, createFirstWorksheetSelector } from '../Core';
import WorksheetGrid from '../Core/WorksheetGrid/WorksheetGrid';
import { WorksheetGridWorksheetStatusIcon } from '../Core/WorksheetGridWorksheetStatusIcon';
import { wetMassAutoFocus } from '../Core/types';
import { EntryComponent } from '../types';
import { GrainSizeTestTypeIds, NominalDiameterTestTypeIds } from './Config';
import './MoistureContentWetMassWorksheetGridEntry.scss';

let MoistureContentWetMassWorksheetGridEntry: React.FC<EntryComponent> = ({ hidden = false }) => {
  /**
   * Currently all worksheets in the grid must come from the same laboratory
   * We assume that the laboratory data attached to the first worksheet applies to all worksheets.
   * (In future, we should fetch laboratory data independently from the worksheet model
   * so we have this whether or not a test is returned)
   */
  const showConstantDryMassEntryFields = useSelector(
    pipe(createDryMassEntryOptionSelector(createFirstWorksheetSelector), (o) => o !== 'Never')
  );

  /**
   * Paths of all fields we need to display messages for
   */
  const messagePaths = [
    `data.MoistureContentTest.Balance`,
    `data.MoistureContentTest.Oven`,
    `data.MoistureContentTest.GrainSize`,
    `data.MoistureContentTest.VisualNominalParticleSize`,
    `data.DryingTray.ContainerLabel`,
    `data.DryingTray.ContainerMass`,
    'data.DryingTray.WetSoilMass',
    'data.DryingTray.WetSoilAndContainerMass',
    'data.MoistureContentTest.TotalWetMass',
    ...(showConstantDryMassEntryFields ? [`data.DryingTray.DateTimePlacedInOvenLocal`] : []),
  ];

  /**
   * Paths of all "common data" fields that must be entered
   * before data can be recorded for individual worksheets
   */
  const requiredCommonData = [
    'MoistureContentTest.Balance',
    ...(showConstantDryMassEntryFields ? ['DryingTray.DateTimePlacedInOvenLocal'] : []),
  ];

  /**
   * Only show grain size if any loaded test requires it
   */
  const showGrainSize = useSelector((state) =>
    Object.values(state.worksheetGrid.worksheets).some((w) => GrainSizeTestTypeIds.includes(`${w.syncModel?.type?.Id}`))
  );

  /**
   * Only show Nominal Diameter if any loaded test requires it
   */
  const showNominalDiameter = useSelector((state) =>
    Object.values(state.worksheetGrid.worksheets).some(
      (w) =>
        NominalDiameterTestTypeIds.includes(`${w.syncModel?.type?.Id}`) && w.syncModel?.data?.DryingTray?.Index === 0
    )
  );

  const anyWorksheetLocked = useSelector((state) =>
    Object.values(state.worksheetGrid.worksheets).some((w) => w.status.readonly.locked)
  );

  const anyWorksheetHasMessages = useSelector((state) =>
    Object.values(state.worksheetGrid.worksheets).some(
      (w) => getMessagesForMultiplePaths(w.syncModel, messagePaths).length > 0
    )
  );
  const readyForEntry = useSelector((state) =>
    requiredCommonData.every((k) => hasNonEmptyValue(state.worksheetGrid.commonData[k]))
  );

  const searchText = useSelector((state) => state.worksheetGrid.additionalData['SampleIdFilter']);

  const { state: locationState } = useLocation();
  const navigate = useNavigate();

  const [focusUUID, setFocusedUUID] = useState<Guid | null>(locationState?.focusUUID);

  /**
   * When the Grid has finished loading we want to clear the `focusUUID` so that we
   * don't force focusing on the row after subsequent changes to the grid i.e. refreshing
   * the grid or filtering via the Sample ID. We also clear when the local focusUUID is set
   * to null to handle the case where you are on the grid entry page, scan a barcode and focus on a row.
   */
  useEffect(() => {
    if (focusUUID === null) navigate('.', { replace: true });
  }, [navigate, focusUUID]);

  /**
   * Set the focusUUID in the case where the user scans a barcode for a row while already
   * on the grid entry page. This allows then to use the barcode to find and focus on the row they want.
   */
  useEffect(() => {
    if (hasValue(locationState?.focusUUID)) {
      setFocusedUUID(locationState?.focusUUID);
    }
  }, [locationState]);

  const tareMassFocus: wetMassAutoFocus = (dryMassReadingId, focusRefs, rowIndex, colIndex) => {
    if (dryMassReadingId === focusUUID) {
      if (!hasNonEmptyValue(focusRefs?.[rowIndex]?.[colIndex].current.value)) {
        setFocusedUUID(null);
        return true;
      }
    }
    return false;
  };

  const tareAndWetMassFocus: wetMassAutoFocus = (dryMassReadingId, focusRefs, rowIndex, colIndex) => {
    if (dryMassReadingId === focusUUID) {
      if (hasNonEmptyValue(focusRefs?.[rowIndex]?.[colIndex - 1].current.value)) {
        setFocusedUUID(null);
        return true;
      }
    }
    return false;
  };

  const gridFields = [
    {
      field: (
        <Field
          input={<HighlightedTextOutput />}
          name={`header.Sample.SampleId`}
          highlightSubstrings={splitIntoSearchTokens(searchText)}
        />
      ),
      header: 'Sample ID',
      width: 160,
      cellStyle: { backgroundColor: styles.colourGrey300 },
      sticky: true,
    },
    {
      field: (_: any, worksheetPath: any) => <WorksheetTopLevelLink worksheetPath={worksheetPath} />,
      header: 'Part of',
      width: 200,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
    {
      field: (
        <Field
          input={<Dropdown />}
          disabled={!readyForEntry}
          name={`data.MoistureContentTest.VisualNominalParticleSize`}
          simpleValue
        />
      ),
      header: 'Nominal Diameter (mm)',
      width: 120,
      hidden: !showNominalDiameter,
    },
    {
      field: (
        <Field input={<Dropdown />} disabled={!readyForEntry} name={`data.MoistureContentTest.GrainSize`} simpleValue />
      ),
      header: 'Grain Size',
      width: 120,
      hidden: !showGrainSize,
    },
    {
      field: (worksheet: any) => {
        var isTinAssociated =
          worksheet?.syncModel?.data?.DryingTray?.MoistureTin?.AssociatedTestUUID === worksheet?.syncModel?.data?.Id;
        return (
          <Field
            input={<TextInput />}
            name={`data.DryingTray.ContainerLabel`}
            disabled={!readyForEntry}
            fieldAdditionalClassName={isTinAssociated ? 'MoistureContentWetMassWorksheetGridControls-Tare-withTin' : ''}
          />
        );
      },
      header: 'Tare ID',
      width: 140,
    },
    {
      field: (
        <Field
          input={<NumericInput />}
          name="data.DryingTray.ContainerMass"
          disabled={!readyForEntry}
          measuredUsingStandaloneEquipment={MoistureContentWetMassGridBalanceKey}
        />
      ),
      header: 'Tare Mass (g)',
      width: 140,
      autoFocus: tareMassFocus,
    },
    {
      field: (
        <Field
          input={<NumericInput />}
          name="data.DryingTray.WetSoilAndContainerMass"
          disabled={!readyForEntry}
          measuredUsingStandaloneEquipment={MoistureContentWetMassGridBalanceKey}
        />
      ),
      header: 'Tare & Wet Mass (g)',
      width: 140,
      autoFocus: tareAndWetMassFocus,
    },

    {
      field: (worksheet: any) => <WorksheetGridWorksheetStatusIcon status={worksheet.status} />,
      header: '',
      width: 20,
      hidden: !anyWorksheetLocked,
      cellStyle: { padding: '0px', marginLeft: '-5px' },
    },
    {
      field: (worksheet: any) => (
        <WorksheetCollatedMessagesButton
          messagePaths={messagePaths}
          title={`Messages for ${worksheet?.syncModel?.header?.Sample?.SampleId} ${
            worksheet?.syncModel?.header?.TopLevelTest?.WorksheetType?.ShortName ??
            worksheet?.syncModel?.type?.ShortName
          }`}
        />
      ),
      header: '',
      width: 30,
      hidden: !anyWorksheetHasMessages,
      cellStyle: { padding: '0px', paddingRight: '5px' },
    },
    {
      field: (
        <Field
          input={<TextOutput />}
          name={`data.DryingTray.WetSoilMass`}
          additionalMessagePaths={['data.MoistureContentTest.TotalWetMass']}
        />
      ),
      header: 'Wet Mass (g)',
      width: 120,
      cellStyle: { backgroundColor: styles.colourGrey100 },
    },
  ];

  return hidden ? null : <WorksheetGrid colDefinitions={gridFields} tabDirection={'horizontal'} />;
};
MoistureContentWetMassWorksheetGridEntry = withInjectedProps(MoistureContentWetMassWorksheetGridEntry);

export default MoistureContentWetMassWorksheetGridEntry;
export { MoistureContentWetMassWorksheetGridEntry };
